import React, { useEffect, useRef } from 'react';
import { useReducedMotion } from 'framer-motion';

/**
 * Slow drifting nodes joined by fading lines. Canvas rather than DOM so the
 * node count stays cheap, and the loop pauses when the section scrolls out of
 * view or the tab is hidden.
 */
export default function NeuralField({ className = '', density = 42, opacity = 0.55 }) {
  const canvasRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const context = canvas.getContext('2d');
    let width = 0;
    let height = 0;
    let nodes = [];
    let frame;
    let visible = true;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const LINK_DISTANCE = 130;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      context.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Scale node count to area so small screens stay light.
      const count = Math.max(14, Math.round((width * height) / 26000));
      nodes = Array.from({ length: Math.min(count, density) }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.22,
        vy: (Math.random() - 0.5) * 0.22,
        r: Math.random() * 1.4 + 0.9
      }));
    };

    const draw = () => {
      context.clearRect(0, 0, width, height);

      for (let i = 0; i < nodes.length; i += 1) {
        const node = nodes[i];
        node.x += node.vx;
        node.y += node.vy;

        if (node.x < 0 || node.x > width) node.vx *= -1;
        if (node.y < 0 || node.y > height) node.vy *= -1;

        for (let j = i + 1; j < nodes.length; j += 1) {
          const other = nodes[j];
          const dx = node.x - other.x;
          const dy = node.y - other.y;
          const distance = Math.hypot(dx, dy);

          if (distance < LINK_DISTANCE) {
            context.strokeStyle = `rgba(63,140,255,${(1 - distance / LINK_DISTANCE) * 0.22})`;
            context.lineWidth = 0.6;
            context.beginPath();
            context.moveTo(node.x, node.y);
            context.lineTo(other.x, other.y);
            context.stroke();
          }
        }

        context.fillStyle = 'rgba(99,150,255,0.5)';
        context.beginPath();
        context.arc(node.x, node.y, node.r, 0, Math.PI * 2);
        context.fill();
      }

      frame = requestAnimationFrame(draw);
    };

    const start = () => {
      if (frame) return;
      frame = requestAnimationFrame(draw);
    };

    const stop = () => {
      if (!frame) return;
      cancelAnimationFrame(frame);
      frame = undefined;
    };

    resize();

    if (prefersReducedMotion) {
      // Render one static frame so the texture is still there, minus the motion.
      nodes.forEach((node) => {
        context.fillStyle = 'rgba(99,150,255,0.45)';
        context.beginPath();
        context.arc(node.x, node.y, node.r, 0, Math.PI * 2);
        context.fill();
      });
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
        if (visible && !document.hidden) start();
        else stop();
      },
      { threshold: 0 }
    );
    observer.observe(canvas);

    const handleVisibility = () => {
      if (document.hidden || !visible) stop();
      else start();
    };

    const handleResize = () => {
      resize();
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('resize', handleResize);
    start();

    return () => {
      stop();
      observer.disconnect();
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('resize', handleResize);
    };
  }, [density, prefersReducedMotion]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{ opacity }}
      className={`pointer-events-none absolute inset-0 h-full w-full ${className}`}
    />
  );
}
